import React, { useState, useEffect, useRef, useLayoutEffect } from "react"
import { useLocation, useHistory } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import {
  changeDeliveryAddress,
  changeDeliveryService,
  clearDeliveryAddress,
  clearDeliveryInfo,
  clearOrderTableNumber,
  deleteAllOrderItems,
  deleteOrderForEditDetails,
  setDeliveryAreaName
} from "@redux"
import { Navbar } from "components"
import { cdsBroadcastChannel, getCheckoutPaymentInfo, validateCartContents } from "utils"
import CheckoutCashTypes from "./CheckoutCashTypes"
import CheckoutPayButton from "./CheckoutPayButton"
import CheckoutSummary from "./CheckoutSummary"
import CheckoutDeilveryType from "./CheckoutDeilveryType"
import CheckoutSelectUser from "./CheckoutSelectUser"
import CheckoutCouponValidation from "./CheckoutCouponValidation"
import useSnapboxQuatation from "./useSnapboxQuatation"
import CheckoutDeliveryServices from "./CheckoutDeliveryServices"
import CheckoutSplitButton from "./CheckoutSplitButton"
import CheckoutOrderItem from "./CheckoutOrderItem"
import CheckoutPaymentList from "./CheckoutPaymentList"
import CheckoutOrderCombo from "./CheckoutOrderCombo"
import CheckoutPlaceOrder from "./CheckoutPlaceOrder"
import CheckoutPrintCustomerReceipt from "./CheckoutPrintCustomerReceipt"
import CheckoutSendPrintCustomerReceiptToPos from "./CheckoutSendPrintCustomerReceiptToPos"
import CheckoutNote from "./CheckoutNote"
import classes from "./index.module.scss"

const Checkout = () => {
  const [t] = useTranslation()

  const history = useHistory()

  const location = useLocation()

  const dispatch = useDispatch()

  const order =
    useSelector(state => state.order)

  const orderForEditDetails =
    useSelector(state =>
      state.orderForEditDetails
    )

  const isOnline =
    useSelector(state => !state.internetStatus.forceOffline) &&
    !orderForEditDetails?.offlineOrderId

  const {
    deliveryAddress,
    deliveryService,
    deliveryType
  } = useSelector(state => state.deliveryInfo)

  const locationOptions =
    useSelector(state => state.systemSettings.locationOptions)

  const [note, setNote] = useState("")
  const [discounts, setDiscounts] = useState([])
  const [cashTypes, setCashTypes] = useState([])
  const [isLinkPay, setIsLinkPay] = useState(false)
  const [foodsPriceChanged, setFoodsPriceChanged] = useState(false)
  const [multiplePayment, setMultiplePayment] = useState(false)
  const [focusedCashType, setFocusedCashType] = useState(null)
  const [selectedOrderItems, setSelectedOrderItems] = useState([])
  const [selectedOrderCombos, setSelectedOrdersCombos] = useState([])
  const [internalDeliveryFee, setInternalDeliveryFee] = useState(null)
  const [selectedUserAccount, setSelectedUserAccount] = useState(null)
  const [selectedSnapboxDeliveryCategory, setSelectedSnapboxDeliveryCategory] = useState("")
  const [printCustomerReceipt, setPrintCustomerReceipt] = useState(true)
  const [sendPrintCustomerReceiptToPos, setSendPrintCustomerReceiptToPos] = useState(true)

  const unmount = useRef(false)
  const paymentScrollRef = useRef(null)

  const {
    snapboxDeliveryFee,
    snapboxQuotaionLoading,
    isSnapboxOutOfService,
    snapboxDeliveryCategories
  } = useSnapboxQuatation(
    selectedUserAccount,
    setDiscounts,
    selectedSnapboxDeliveryCategory,
    setSelectedSnapboxDeliveryCategory
  )

  const isOrderSplited = () => {
    if (!orderForEditDetails) return false

    const subPaymentItems =
      orderForEditDetails.subPaymentItems

    const orderItemIds =
      Object.keys(subPaymentItems)

    return orderItemIds.some(orderItemId =>
      subPaymentItems?.[orderItemId]?.paymentStatus === "paid"
    )
  }

  const filterPaidOrderItems = orderItems => {
    if (!orderForEditDetails) return orderItems

    const subPaymentItems =
      orderForEditDetails.subPaymentItems

    return orderItems.filter(orderItem =>
      subPaymentItems?.[orderItem.orderItemId]?.paymentStatus !== "paid"
    )
  }

  const filterPaidCombos = orderCombos => {
    if (!orderForEditDetails) return orderCombos

    const subPaymentItems =
      orderForEditDetails.subPaymentItems

    return orderCombos.filter(orderCombo =>
      !orderCombo.items.every(comboItem =>
        subPaymentItems?.[comboItem.orderItemId]?.paymentStatus === "paid"
      )
    )
  }

  const clearCart = () => {
    dispatch(clearDeliveryInfo())

    dispatch(deleteAllOrderItems())

    dispatch(
      deleteOrderForEditDetails()
    )

    dispatch(
      clearOrderTableNumber()
    )

    if (unmount.current) return

    setCashTypes([])

    setDiscounts([])

    setSelectedOrderItems([])

    setSelectedUserAccount(null)
  }

  const handleGoBack = () => {
    if (!!orderForEditDetails) {
      dispatch(
        deleteAllOrderItems()
      )

      dispatch(
        deleteOrderForEditDetails()
      )
    }

    dispatch(
      clearDeliveryAddress()
    )

    dispatch(
      clearDeliveryInfo()
    )

    dispatch(
      clearOrderTableNumber()
    )

    history.push("/foods")
  }

  const setInternalDeliveryFeesForOrders = () => {
    if (!deliveryAddress.address) {
      return setInternalDeliveryFee(null)
    }

    if (!deliveryAddress._id) return

    if (!locationOptions?.[0]) return

    const locationOption = locationOptions[0]

    setInternalDeliveryFee({
      fee: locationOption.deliveryFee
    })

    dispatch(
      setDeliveryAreaName(
        locationOption.areaName,
        locationOption.deliveryFee
      )
    )
  }

  const fillForm = () => {
    if (!orderForEditDetails) return

    setSelectedUserAccount(
      orderForEditDetails.selectedUserAccount
    )

    setDiscounts(
      orderForEditDetails.discounts
    )

    if (orderForEditDetails.deliveryType !== "delivery") return

    dispatch(
      changeDeliveryAddress(
        orderForEditDetails.address
      )
    )

    dispatch(
      changeDeliveryService(
        orderForEditDetails.deliveryService
      )
    )
  }

  const validateCart = async () => {
    if (orderForEditDetails === null) return
    if (orderForEditDetails.isOffline) return

    const previousOrder = {
      items: orderForEditDetails.items,
      combos: orderForEditDetails.combos
    }

    const problematicItems =
      await validateCartContents(
        order.items,
        order.combos,
        previousOrder
      )

    setFoodsPriceChanged(
      problematicItems.length > 0
    )
  }

  const autoCorrectCashTypesWhenCartChanged = () => {
    setCashTypes(prev => {
      const newCashTypes =
        prev.map((cashType, ctIndex) =>
          ctIndex === 0
            ?
            {
              ...cashType,
              amount: total
            }
            :
            {
              ...cashType,
              amount: 0
            }
        )

      return newCashTypes
    })
  }

  const sendEventsToCds = () => {
    const bc = cdsBroadcastChannel.get()

    if (!bc) return

    bc.postMessage({
      type: "checkoutChanges",
      orderItems,
      discounts,
      deliveryFees,
      deliveryType
    })
  }

  // restore checkout page when redirect back from kitchen tables page
  const restorCheckoutPageState = () => {
    if (!location.state?.previousChechkoutStates) return

    const { previousChechkoutStates } = location.state

    setNote(previousChechkoutStates.note)
    setIsLinkPay(previousChechkoutStates.isLinkPay)
    setMultiplePayment(previousChechkoutStates.multiplePayment)
    setFocusedCashType(previousChechkoutStates.focusedCashType)
    setPrintCustomerReceipt(previousChechkoutStates.printCustomerReceipt)
    setSendPrintCustomerReceiptToPos(previousChechkoutStates.sendPrintCustomerReceiptToPos)
    setSelectedUserAccount(previousChechkoutStates.selectedUserAccount)
    setDiscounts(prev => {
      const filteredDiscounts =
        previousChechkoutStates.discounts.filter(d =>
          !prev.some(dis => dis.couponCode === d.couponCode)
        )

      return [...prev, ...filteredDiscounts]
    })

    setTimeout(() => {
      if (unmount.current) return

      setCashTypes(previousChechkoutStates.cashTypes)
      setFocusedCashType(previousChechkoutStates.focusedCashType)
    }, 100)
  }

  useEffect(() => {
    setInternalDeliveryFeesForOrders()
  }, [isOnline, deliveryAddress])

  useEffect(() => {
    deliveryType !== "dineIn" &&
      setSelectedOrderItems([])
  }, [isOnline, deliveryType])

  useEffect(() => {
    isOnline && validateCart()
  }, [isOnline])

  useLayoutEffect(() => {
    fillForm()
  }, [orderForEditDetails])

  useEffect(() => {
    autoCorrectCashTypesWhenCartChanged()
  }, [
    discounts,
    deliveryType,
    selectedOrderItems,
    selectedOrderCombos,
    snapboxDeliveryFee,
    internalDeliveryFee
  ])

  useEffect(() => {
    sendEventsToCds()
  }, [
    discounts,
    deliveryType,
    internalDeliveryFee,
    snapboxDeliveryFee
  ])

  useEffect(() => {
    unmount.current = false

    restorCheckoutPageState()

    return () => unmount.current = true
  }, [])

  const splitEnabled =
    selectedOrderItems.length > 0 ||
    selectedOrderCombos.length > 0

  const orderHasSplited = isOrderSplited()

  const orderItems =
    splitEnabled
      ? selectedOrderItems
      : filterPaidOrderItems(order.items)

  const orderCombos =
    splitEnabled
      ? selectedOrderCombos
      : filterPaidCombos(order.combos)

  const deliveryFees =
    deliveryType === "delivery"
      ? deliveryService === "internal" ? internalDeliveryFee : snapboxDeliveryFee
      : null

  const {
    total,
    rounding,
    subtotal,
    totalTax,
    totalCharges,
    totalDiscount,
    taxesAndCharges,
    totalFoodDiscounts,
    totalSystemDiscounts,
    totalDeliveryDiscountByCoupon,
    totalDeliveryFeeBeforeDiscount
  } = getCheckoutPaymentInfo(
    orderItems,
    orderCombos,
    discounts,
    deliveryFees,
    deliveryType
  )

  const deliveryServiceOutOfRange =
    deliveryType === "delivery" &&
    deliveryService === "snapbox" &&
    isSnapboxOutOfService

  const doesOrderHasOneItem =
    (order.items.length + order.combos.length) === 1

  const selectSomeOrderItemForSplit =
    selectedOrderItems.length > 0 ||
    selectedOrderCombos.length > 0

  const didNotSelectAllOrderItemForSplit =
    selectedOrderItems.length !== order.items.length ||
    selectedOrderCombos.length !== order.combos.length

  const isOrderEditable =
    !orderHasSplited &&
    !splitEnabled &&
    !foodsPriceChanged

  const orderHasCoupons = discounts.length > 0

  const showDeliveryServices =
    !orderHasSplited &&
    deliveryType === "delivery" &&
    deliveryAddress.address

  const isGuestOrder =
    !!orderForEditDetails?.isGuestOrder

  const showSplitButton =
    orderHasSplited ||
    (
      isOnline &&
      selectSomeOrderItemForSplit &&
      didNotSelectAllOrderItemForSplit
    )

  const showCheckoutButton =
    !showSplitButton &&
    location.state?.mode !== "placeOrder"

  const showPlaceOrderButton =
    location.state?.mode === "placeOrder"

  const title =
    location.state?.mode === "placeOrder"
      ? t("ایجاد_سفارش")
      : t("پرداخت_نهایی")

  return (
    <div className={classes["container"]}>
      <Navbar
        title={title}
        variant="compact"
        onLeftIconPress={handleGoBack}
      />
      <div className={classes["wrapper"]}>
        <div className={classes["section"]}>
          {
            !!orderForEditDetails?.orderCode &&
            <div className={classes["code"]}>
              {t("کد_سفارش")}:{" "}
              <b>{orderForEditDetails.orderCode}</b>
            </div>
          }

          <div className={classes["order-items"]}>
            {order.items.map(orderItem => (
              <CheckoutOrderItem
                key={orderItem.orderItemId}
                doesOrderHasOneItem={doesOrderHasOneItem}
                orderItem={orderItem}
                selectedOrderItems={selectedOrderItems}
                setSelectedOrderItems={setSelectedOrderItems}
                orderHasCoupons={orderHasCoupons}
              />
            ))}

            {order.combos.map((orderCombo, orderComboIndex) =>
              <CheckoutOrderCombo
                key={orderComboIndex}
                doesOrderHasOneItem={doesOrderHasOneItem}
                orderCombo={orderCombo}
                selectedOrderCombos={selectedOrderCombos}
                setSelectedOrdersCombos={setSelectedOrdersCombos}
                orderHasCoupons={orderHasCoupons}
              />
            )}
          </div>

          <CheckoutSummary
            total={total}
            rounding={rounding}
            subtotal={subtotal}
            totalTax={totalTax}
            totalDiscount={totalDiscount}
            totalCharges={totalCharges}
            deliveryFees={deliveryFees}
            taxesAndCharges={taxesAndCharges}
            totalFoodDiscounts={totalFoodDiscounts}
            totalSystemDiscounts={totalSystemDiscounts}
            totalDeliveryDiscountByCoupon={totalDeliveryDiscountByCoupon}
            totalDeliveryFeeBeforeDiscount={totalDeliveryFeeBeforeDiscount}
          />
        </div>

        <div className={classes["center-container"]}>
          <CheckoutDeilveryType
            isGuestOrder={isGuestOrder}
            orderHasSplited={orderHasSplited}
            isOrderEditable={isOrderEditable}
            note={note}
            discounts={discounts}
            cashTypes={cashTypes}
            isLinkPay={isLinkPay}
            multiplePayment={multiplePayment}
            focusedCashType={focusedCashType}
            printCustomerReceipt={printCustomerReceipt}
            sendPrintCustomerReceiptToPos={sendPrintCustomerReceiptToPos}
            selectedUserAccount={selectedUserAccount}
          />

          {
            showDeliveryServices &&
            <CheckoutDeliveryServices
              isSnapboxOutOfService={isSnapboxOutOfService}
              snapboxQuotaionLoading={snapboxQuotaionLoading}
              internalDeliveryFee={internalDeliveryFee}
              setInternalDeliveryFee={setInternalDeliveryFee}
              snapboxDeliveryFee={snapboxDeliveryFee}
              snapboxDeliveryCategories={snapboxDeliveryCategories}
              selectedSnapboxDeliveryCategory={selectedSnapboxDeliveryCategory}
              setSelectedSnapboxDeliveryCategory={setSelectedSnapboxDeliveryCategory}
            />
          }

          <CheckoutSelectUser
            isGuestOrder={isGuestOrder}
            isOrderEditable={isOrderEditable}
            setDiscounts={setDiscounts}
            selectedUserAccount={selectedUserAccount}
            setSelectedUserAccount={setSelectedUserAccount}
          />

          <CheckoutCouponValidation
            isOrderEditable={isOrderEditable}
            orderItems={orderItems}
            discounts={discounts}
            setDiscounts={setDiscounts}
            deliveryFees={deliveryFees}
            selectedUserAccount={selectedUserAccount}
          />

          {
            !splitEnabled &&
            <CheckoutNote
              note={note}
              setNote={setNote}
            />
          }

          <CheckoutCashTypes
            total={total}
            cashTypes={cashTypes}
            setCashTypes={setCashTypes}
            isLinkPay={isLinkPay}
            setIsLinkPay={setIsLinkPay}
            paymentScrollRef={paymentScrollRef}
            setFocusedCashType={setFocusedCashType}
            multiplePayment={multiplePayment}
            setMultiplePayment={setMultiplePayment}
          />
        </div>

        <div className={classes["section"]}>
          <CheckoutPaymentList
            total={total}
            isLinkPay={isLinkPay}
            cashTypes={cashTypes}
            setCashTypes={setCashTypes}
            focusedCashType={focusedCashType}
            setFocusedCashType={setFocusedCashType}
            paymentScrollRef={paymentScrollRef}
          />

          <div className={classes["checkboxes"]}>
            <CheckoutSendPrintCustomerReceiptToPos
              cashTypes={cashTypes}
              sendPrintCustomerReceiptToPos={sendPrintCustomerReceiptToPos}
              setSendPrintCustomerReceiptToPos={setSendPrintCustomerReceiptToPos}
            />

            <CheckoutPrintCustomerReceipt
              printCustomerReceipt={printCustomerReceipt}
              setPrintCustomerReceipt={setPrintCustomerReceipt}
            />
          </div>

          {
            showSplitButton &&
            <CheckoutSplitButton
              total={total}
              onComplete={clearCart}
              splitEnabled={splitEnabled}
              orderItems={orderItems}
              orderCombos={orderCombos}
              cashTypes={cashTypes}
              totalTax={totalTax}
              totalCharges={totalCharges}
              totalDiscount={totalDiscount}
              selectedUserAccount={selectedUserAccount}
              printCustomerReceipt={printCustomerReceipt}
              sendPrintCustomerReceiptToPos={sendPrintCustomerReceiptToPos}
            />
          }

          {
            showCheckoutButton &&
            <CheckoutPayButton
              note={note}
              total={total}
              isLinkPay={isLinkPay}
              onComplete={clearCart}
              totalTax={totalTax}
              isOnline={isOnline}
              totalDiscount={totalDiscount}
              totalCharges={totalCharges}
              cashTypes={cashTypes}
              discounts={discounts}
              orderItems={orderItems}
              orderCombos={orderCombos}
              deliveryFees={deliveryFees}
              deliveryServiceOutOfRange={deliveryServiceOutOfRange}
              snapboxQuotaionLoading={snapboxQuotaionLoading}
              selectedUserAccount={selectedUserAccount}
              printCustomerReceipt={printCustomerReceipt}
              sendPrintCustomerReceiptToPos={sendPrintCustomerReceiptToPos}
            />
          }

          {
            showPlaceOrderButton &&
            <CheckoutPlaceOrder
              onComplete={clearCart}
              note={note}
              isOnline={isOnline}
              discounts={discounts}
              orderItems={orderItems}
              orderCombos={orderCombos}
              deliveryFees={deliveryFees}
              deliveryServiceOutOfRange={deliveryServiceOutOfRange}
              snapboxQuotaionLoading={snapboxQuotaionLoading}
              selectedUserAccount={selectedUserAccount}
              printCustomerReceipt={printCustomerReceipt}
              sendPrintCustomerReceiptToPos={sendPrintCustomerReceiptToPos}
            />
          }
        </div>
      </div>
    </div>
  )
}

export default Checkout